<script module lang="ts">
	import { PUBLIC_MAPBOX_ACCESS_TOKEN } from '$env/static/public';
	import mapbox, { type Map } from 'mapbox-gl';
	import { extent } from 'd3';
	import brazilRegionData from '$lib/data/brazil_regions_cancer_deaths.json';

	mapbox.accessToken = PUBLIC_MAPBOX_ACCESS_TOKEN;

	const mapStyle = 'mapbox://styles/akakkarcc/cmcqyrj8m002301r4g4hp956j';
	const defaultView = { zoom: 3.64, center: [-56.114354, -14.269212] as [number, number] };
	const boundingBoxBrazil = [
		-76,
		-33.7683777809, // Southwest coordinates
		-34,
		5.24448639569 // Northeast coordinates
	] as [number, number, number, number];

	const boundingBoxWorld = [-136.882782, -74.536557, 32.036819, 47.691739] as [
		number,
		number,
		number,
		number
	];

	export const colorExtents = extent(
		brazilRegionData.features,
		(d) => d.properties.cancer_death_proportion_2019
	);

	export const loaded = $state({ value: false });
</script>

<script lang="ts">
	import { onMount, onDestroy } from 'svelte';
	import { getCopy } from '$lib/lang/copy.svelte';
	import { md } from '$lib/global.svelte';
	import { fade } from 'svelte/transition';
	import Picture from '$lib/components/media/Picture.svelte';

	const copy = $derived(getCopy().scrolly);

	let { step } = $props();

	let map: Map | null = $state(null);
	let mapContainer: HTMLElement | null = $state(null);
	let rotationFrame: number | null = $state(null);

	const padding = $derived({
		left: md.current ? 200 : 0
	});

	const boundingBox = $derived(step === 0 ? boundingBoxWorld : boundingBoxBrazil);

	onMount(async () => {
		if (!mapbox.supported()) {
			console.log('Your browser does not support Mapbox GL');
			return;
		}

		if (!mapContainer) return;

		map = new mapbox.Map({
			style: mapStyle,
			container: mapContainer,
			attributionControl: false,
			cooperativeGestures: true,
			...defaultView
		});

		await map.once('load');

		// Add GeoJSON data source
		map.addSource('brazil-regions', {
			type: 'geojson',
			data: brazilRegionData
		});

		// Add fill layer
		map.addLayer({
			id: 'brazil-regions-fill',
			type: 'fill',
			source: 'brazil-regions',
			paint: {
				'fill-color': [
					'interpolate',
					['linear'],
					['get', 'cancer_death_proportion_2019'],
					colorExtents[0],
					'#ffcccc', // Light red for lower values
					colorExtents[1],
					'#ff5054' // Red for higher values
				]
			},
			layout: {
				visibility: 'none'
			}
		});

		// Add outline layer
		map.addLayer({
			id: 'brazil-regions-outline',
			type: 'line',
			source: 'brazil-regions',
			paint: {
				'line-color': '#faf4f0',
				'line-width': 1
			},
			layout: {
				visibility: 'none'
			}
		});

		map.on('resize', () => {
			map?.fitBounds(boundingBox, {
				padding
			});
		});

		map.scrollZoom.disable();
		map.dragRotate.disable();
		map.touchZoomRotate.disableRotation();

		loaded.value = true;
	});

	$effect(() => {
		if (!map) return;

		// Zoom to Brazil's bounding box
		map.fitBounds(boundingBox, {
			padding
		});
	});

	// Handle rotation based on step
	$effect(() => {
		if (!loaded.value) return;

		if (step === 0) {
			if (!rotationFrame) {
				const animate = () => {
					if (map && rotationFrame && !map.isMoving()) {
						const center = map.getCenter();
						center.lng = (center.lng + 0.05) % 360;
						map.setCenter(center);
					}
					rotationFrame = requestAnimationFrame(animate);
				};
				rotationFrame = requestAnimationFrame(animate);
			}
		} else {
			if (rotationFrame) {
				cancelAnimationFrame(rotationFrame);
				rotationFrame = null;
			}
		}
	});

	// Set layer visibility based on step
	$effect(() => {
		if (!loaded.value || !map) return;

		if (step <= 5) {
			map.setLayoutProperty('brazil-regions-fill', 'visibility', 'none');
			map.setLayoutProperty('brazil-regions-outline', 'visibility', 'none');
		} else {
			map.setLayoutProperty('brazil-regions-fill', 'visibility', 'visible');
			map.setLayoutProperty('brazil-regions-outline', 'visibility', 'visible');
		}
	});

	onDestroy(() => {
		if (rotationFrame) {
			cancelAnimationFrame(rotationFrame);
		}
		if (map) map.remove();
	});
</script>

<figure class={['bg-blue relative h-[100dvh] w-full overflow-hidden']}>
	<div
		class={[
			'absolute inset-0 transition-transform duration-700',
			{ 'max-md:translate-y-1/3 md:translate-x-1/3': step === 0 }
		]}
	>
		<div class="h-full w-full" bind:this={mapContainer}></div>

		{#if step === 0}
			<div class=" absolute inset-0 flex items-center justify-center md:inset-1/4" transition:fade>
				<Picture src="grid.webp" alt={copy.alt} class="h-full w-full object-cover" />
			</div>
		{/if}
	</div>
</figure>

<style lang="postcss">
	@reference '$lib/styles/app.css';

	:global(.mapboxgl-ctrl) {
		@apply !hidden;
	}
</style>
